/**
 * Change Password page
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Input, Alert } from '../components/ui';
import { validatePassword } from '../utils/validation';

export function ChangePassword() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error || 'Password does not meet requirements');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      await userService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      await refresh();
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <Card>
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-white">Change Password</h1>
              <p className="text-gray-400 text-sm mt-2">
                For security, you must set a new password before continuing.
              </p>
            </div>

            {error && (
              <Alert variant="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                helpText="Minimum 12 characters"
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}
